export type ShpyCpxHandlerInput = {
  browser: CpxBrowser;
  event: CpxEvent;
  init: CpxInit;
};

export enum CustomPixelStdEventName {
  CART_VIEWED = 'CART_VIEWED',
  // CART_VIEWED = 'cart_viewed',
  CHECKOUT_ADDRESS_INFO_SUBMITTED = 'checkout_address_info_submitted',
  CHECKOUT_COMPLETED = 'checkout_completed',
  CHECKOUT_CONTACT_INFO_SUBMITTED = 'checkout_contact_info_submitted',
  CHECKOUT_SHIPPING_INFO_SUBMITTED = 'checkout_shipping_info_submitted',
  CHECKOUT_STARTED = 'checkout_started',
  COLLECTION_VIEWED = 'collection_viewed',
  PAGE_VIEWED = 'page_viewed',
  PAYMENT_INFO_SUBMITTED = 'payment_info_submitted',
  PRODUCT_ADDED_TO_CART = 'product_added_to_cart',
  PRODUCT_REMOVED_FROM_CART = 'product_removed_from_cart',
  PRODUCT_VIEWED = 'product_viewed',
  SEARCH_SUBMITTED = 'search_submitted',
  UNKNOWN_EVENT = 'unknown_event',
}

export type CpxEvent = {
  clientId: string;
  context: CpxContext;
  data: CpxData;
  id: string;
  name: CustomPixelStdEventName;
  timestamp: string;
  type: string;
};

export type CpxData = {
  cart?: CpxCart;
  cartLine?: CpxCartLine;
  checkout?: CpxCheckout;
  element?: CpxFormElement;
  productVariant?: CpxMerchandise;
};

export type CpxFormElement = {
  action: string;
  elements: CpxInputElements[];
  id: string;
};

export type CpxInputElements = {
  id: string;
  name: string;
  tagName: string;
  type: string;
  value: string;
};

export type CpxCartLine = {
  cost: CpxCost;
  merchandise: CpxMerchandise;
  quantity: number;
};

export type CpxCost = {
  totalAmount: CpxTotalAmount;
};

export type CpxTotalAmount = {
  amount: number;
  currencyCode: string;
};

export type CpxMerchandise = {
  id: string;
  image: CpxImage;
  price: CpxPrice;
  product: CpxProduct;
  sku?: string;
  title?: string;
  untranslatedTitle: any;
};

export type CpxImage = {
  src: any;
};

export type CpxPrice = {
  amount: number;
  currencyCode: string;
};

export type CpxProduct = {
  id: string;
  sku?: string;
  title: string;
  type: string;
  untranslatedTitle: string;
  url: string;
  vendor: string;
};

export type CpxCheckout = {
  // cart: CpxCart;
  attributes: any[];
  billingAddress: CpxBillingAddress;
  currencyCode: string;
  discountApplications: CpxDiscountApplication[];
  discountsAmount: {
    amount: number;
    currencyCode: string;
  };
  email: string | null;
  lineItems: CpxLineItem[];
  localization: any;
  order: CpxOrder;
  phone: string | null;
  shippingAddress: CpxShippingAddress;
  shippingLine: CpxShippingLine;
  smsMarketingPhone: string | null | undefined;
  subtotalPrice: CpxSubtotalPrice;
  token: string;
  totalPrice: CpxTotalPrice;
  totalTax: CpxTotalTax;
  transactions: CpxTransaction[];
};

export type CpxDiscountAllocation = {
  amount: {
    amount: number;
    currencyCode: string;
  };
  discountApplication: CpxDiscountApplication;
};

export type CpxDiscountApplication = {
  allocationMethod: string;
  targetSelection: string;
  targetType: string;
  title: string;
  type: string;
  value: {
    any?: number;
  };
};

export type CpxBillingAddress = {
  address1: string;
  address2?: string | null;
  city: string;
  country: string;
  countryCode: string;
  email: string | null | undefined;
  firstName: string;
  lastName: string;
  phone: string | null;
  province: string | null;
  provinceCode?: string | null;
  zip: string;
};

export type CpxLineItem = {
  discountAllocations: CpxDiscountAllocation[];
  discountsAmount: {
    amount: number;
    currencyCode: string;
  };
  id: string;
  quantity: number;
  sellingPlanAllocation?: CpxSellingPlanAllocation;
  title: string;
  variant: CpxVariant;
};

export type CpxSellingPlanAllocation = {
  sellingPlan: CpxSellingPlan;
};

export type CpxSellingPlan = {
  name: string;
};

export type CpxVariant = {
  id: string;
  image: CpxImage;
  price: CpxPrice;
  product: CpxProduct;
  sku?: string;
  title?: string;
  untranslatedTitle: string;
};

export type CpxOrder = {
  customer: {
    firstName: string | null | undefined;
    id: string;
    lastName: string | null | undefined;
  };
  id: string;
};

export type CpxShippingAddress = {
  address1: string;
  address2?: string | null;
  city: string;
  country: string;
  countryCode: string;
  email: string | null | undefined;
  firstName: string;
  lastName: string;
  phone: string | null;
  province: string | null;
  provinceCode?: string | null;
  zip: string;
};

export type CpxSubtotalPrice = {
  amount: number;
  currencyCode: string;
};

export type CpxShippingLine = {
  price: CpxPrice;
};

export type CpxTotalTax = {
  amount: number;
  currencyCode: string;
};

export type CpxTotalPrice = {
  amount: number;
  currencyCode: string;
};

export type CpxTransaction = {
  amount: CpxAmount;
  gateway: string;
  paymentMethod: CpxPaymentMethod;
};

export type CpxPaymentMethod = {
  name?: string;
  type?: string;
};

export type CpxAmount = {
  amount: number;
  currencyCode: string;
};

export type CpxContext = {
  document: CpxDocument;
  navigator: CpxNavigator;
  window: CpxWindow;
};

export type CpxDocument = {
  characterSet?: string;
  location: CpxLocation;
  referrer?: string;
  title?: string;
};

export type CpxLocation = {
  characterSet?: string;
  hash?: string;
  host?: string;
  hostname?: string;
  href: string;
  origin?: string;
  pathname?: string;
  port?: string;
  protocol?: string;
  referrer?: string;
  search?: string;
  title?: string;
};

export type CpxNavigator = {
  cookieEnabled: boolean;
  language: string;
  languages: string[];
  userAgent: string;
};

export type CpxWindow = {
  innerHeight: number;
  innerWidth: number;
  location: CpxLocation;
  origin: string;
  outerHeight: number;
  outerWidth: number;
  pageXOffset: number;
  pageYOffset: number;
  screen: CpxScreen;
  screenX: number;
  screenY: number;
  scrollX: number;
  scrollY: number;
};

export type CpxScreen = {
  height: number;
  width: number;
};

export type CpxBrowser = {
  cookie: CpxCookie;
  localStorage: CpxLocalStorage;
  sessionStorage: CpxSessionStorage;
};

export type CpxCookie = object;

export type CpxLocalStorage = object;

export type CpxSessionStorage = object;

export type CpxInit = {
  context: CpxContext;
  customerPrivacy: any;
  data: CpxInitData;
};

export type CpxInitData = {
  cart: CpxCart | null;
  customer: CpxCustomer | null;
  shop: CpxShop | null;
};

export type CpxShop = {
  countryCode: string;
  myshopifyDomain: string;
  name: string;
  paymentSettings: CpxPaymentSettings;
  storefrontUrl: string;
};
export type CpxPaymentSettings = {
  currencyCode: string;
};

export type CpxCustomer = {
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  email?: string;
  firstName?: string;
  id?: string;
  lastName?: string;
  ordersCount: string;
  phone?: string;
  province?: string;
  provinceCode?: string;
  zip?: string;
};

export type CpxCart = {
  cost: CpxCost;
  id: string;
  lines: CpxLine[];
  totalQuantity: number;
};

export type CpxLine = {
  cost: CpxCost;
  merchandise: CpxMerchandise;
  quantity: number;
};
